import { createAction } from 'redux-starter-kit';
import { get, post } from '../../../../utility/via-fetch';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import { putDamageTypes, putDamageType } from '../../../domain/damageTypes/damageTypeActions';
import { putModelTypes } from '../../../domain/modelTypes/modelTypeActions';
import { putDamageArea, putDamageAreas } from '../../../domain/damageAreas/damageAreaActions';
import { addMessage } from '../../application/messageUiActions';

/**
 * An action that signifies a damage area is being currently edited.
 * @example dispatch(editDamageArea(damageAreaCode))
 */
export const editDamageArea = createAction('EDIT_DAMAGE_AREA');

/**
 * An action that signifies no damage area is being edited.
 * @example dispatch(cancelEditDamageArea())
 */
export const cancelEditDamageArea = createAction('CANCEL_EDIT_DAMAGE_AREA');

/**
 * An action that signifies a request to load damage area data has been made.
 */
export const fetchDamageAreasPageData = createAction('FETCH_DAMAGE_AREAS_PAGE_DATA');

export function requestDamageAreasPageData() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchDamageAreasPageData.toString()));
    dispatch(fetchDamageAreasPageData(requestId));
    try {
      const [damageAreas, damageTypes, modelTypes] = await Promise.all([get('/api/damage-areas/'), get('/api/damage-types'), get('/api/model-types')]);

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putDamageAreas(damageAreas));
      dispatch(putDamageTypes(damageTypes));
      dispatch(putModelTypes(modelTypes));
      dispatch(requestSucceded(requestId, damageAreas.map(area => area.damageAreaCode)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies a request to load damage areas has been made.
 * @example dispatch(fetchDamageAreas())
 */
export const fetchDamageAreas = createAction('FETCH_DAMAGE_AREAS');

export function requestDamageAreas() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchDamageAreas.toString()));
    dispatch(fetchDamageAreas(requestId));
    try {
      const damageAreas = await get('/api/damage-areas/');

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putDamageAreas(damageAreas));
      dispatch(requestSucceded(requestId, damageAreas.map(area => area.damageAreaCode)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


/**
 * An action that signifies a fetch request to load damage types has been made.
 * @example dispatch(fetchDamageTypes(requestId))
 */
export const fetchDamageTypes = createAction('FETCH_DAMAGE_TYPES');

/**
 * An action for loading damage types in the Damage Area Configuration screen.
 * @example dispatch(requestDamageTypes())
 */
export const requestDamageTypes = () => async (dispatch) => {
  const requestId = dispatch(requestStart(fetchDamageTypes.toString()));
  dispatch(fetchDamageTypes());
  try {
    const damageTypes = await get('/api/damage-types/');

    // Normalise the data, store the object in the domain, and the ids in the request payload.
    dispatch(putDamageTypes(damageTypes));
    dispatch(requestSucceded(requestId));
  } catch (error) {
    dispatch(requestFailed(requestId, error));
  }
};


/**
 * An action that signifies a request for the Model Types has been made.
 * This takes a request id as an argument.
 * @example dispatch(fetchModelTypes(requestId))
 */
export const fetchModelTypes = createAction('FETCH_MODEL_TYPES');

/**
 * An action for loading model types in the Damage Area Configuration screen.
 * @example dispatch(requestModelTypes())
 */
export const requestModelTypes = () => async (dispatch) => {
  const requestId = dispatch(requestStart(fetchModelTypes.toString()));
  dispatch(fetchModelTypes(requestId));

  try {
    const modelTypes = await get('/api/model-types');
    dispatch(putModelTypes(modelTypes));
    dispatch(requestSucceded(requestId));
  } catch (error) {
    dispatch(requestFailed(requestId, error));
  }
};


/**
 * An action that signifies that a fetch to add a damage area has been
 * made in the Damage Area Configuration screen. This should pass through the request id.
 * @example dispatch(fetchDamageArea(requestId))
 */
export const fetchAddDamageArea = createAction('FETCH_CREATE_DAMAGE_AREA');

const createDamageType = async (damageType) => {
  const createdDamageType = { ...damageType };
  createdDamageType.damageTypeCode = `${await post('/api/damage-types', {}, damageType)}`;
  return createdDamageType;
};

const createDamageTypes = async types => Promise.all(types.map(dt => createDamageType(dt)));


/**
 * An action for creating a damage area on the server.
 * @param {object} area A damage area to be created.
 * @param {array} [damageTypesToCreate] Damages to be created before adding the damage area.
 * @example dispatch(requestDamageArea(area));
 */
export function requestAddDamageArea(area, damageTypesToCreate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddDamageArea.toString()));
    dispatch(fetchAddDamageArea(requestId));
    try {
      // Wait for all the damage types to be created.
      const createdDamageTypes = damageTypesToCreate
        ? await createDamageTypes(damageTypesToCreate)
        : [];
      createdDamageTypes.forEach(dt => dispatch(putDamageType(dt)));
      // Construct our new area object
      const createdArea = {
        ...area,
        linkedModelTypeCodes: [...area.linkedModelTypeCodes],
        linkedDamageTypeCodes: [
          ...area.linkedDamageTypeCodes,
          ...createdDamageTypes.map(dt => dt.damageTypeCode),
        ],
      };

      // Create a new damage area with damage types.
      createdArea.damageAreaCode = await post('/api/damage-areas', {}, createdArea);
      dispatch(putDamageArea(createdArea));
      dispatch(cancelEditDamageArea());
      dispatch(requestSucceded(requestId, createdArea.damageAreaCode));
      dispatch(addMessage('success', 'itemSaved', { item: createdArea.damageAreaDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}


/**
 * An action that signifies that a fetch to update a damage area
 * has been made in the Damage Area Configuration screen. This should pass through the request id.
 * @example dispatch(fetchUpdateDamageAreaId(requestId));
 */
export const fetchUpdateDamageArea = createAction('FETCH_UPDATE_DAMAGE_AREA');

/**
 * Updates the damage area with new data.
 * If there are new damage types, then it will first make those damage types.
 * @param {object} area The damage area to be crreated.
 * @param {object[]} [damageTypesToCreate] An array of damage types to create first.
 */
export function requestUpdateDamageArea(area, damageTypesToCreate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart());
    dispatch(fetchUpdateDamageArea(requestId));
    try {
      // Wait for all the damage types to be created.
      const createdDamageTypes = damageTypesToCreate
        ? await createDamageTypes(damageTypesToCreate)
        : [];
      createdDamageTypes.forEach(dt => dispatch(putDamageType(dt)));

      const updatedArea = {
        ...area,
        linkedModelTypeCodes: area.linkedModelTypeCodes ? [...area.linkedModelTypeCodes] : [],
        linkedDamageTypeCodes: [
          ...area.linkedDamageTypeCodes,
          ...createdDamageTypes.map(dt => dt.damageTypeCode),
        ],
      };

      await post(`/api/damage-areas/${area.damageAreaCode}`, {}, updatedArea);
      dispatch(cancelEditDamageArea());
      dispatch(putDamageArea(updatedArea));
      dispatch(requestSucceded(requestId, updatedArea.damageAreaCode));
      dispatch(addMessage('success', 'itemUpdated', { item: updatedArea.damageAreaCode }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export const addPublishDamageArea = createAction('ADD_PENDING_PUBLISH_DAMAGE_AREA');
export const removePublishDamageArea = createAction('REMOVE_PENDING_PUBLISH_DAMAGE_AREA');


export const fetchDamageTypesForArea = createAction('GET_DAMAGE_TYPES_FOR_AREA');

export function requestDamageTypesForArea(area) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchDamageTypesForArea.toString()));
    dispatch(fetchDamageTypesForArea(requestId));
    try {
      const linkedDamageTypeCodes = await get(`/api/damage-areas/${area.damageAreaCode}/damage-types`, {});
      dispatch(putDamageArea({ ...area, linkedDamageTypeCodes }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export function requestPublishDamageArea(area) {
  let toggledArea = { ...area, active: !area.active };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addPublishDamageArea.toString()));
    dispatch(addPublishDamageArea(toggledArea.damageAreaCode));
    try {
      // If they never entered the edit screen, we need to quickly retrieve the existing damage types since  hey were never loaded.
      // This is obviously inefficient, but I'm not too worried about it
      if (!toggledArea.linkedDamageTypeCodes) {
        const linkedDamageTypeCodes = await get(`/api/damage-areas/${area.damageAreaCode}/damage-types`, {});
        toggledArea = { ...toggledArea, linkedDamageTypeCodes };
      }
      await post(`/api/damage-areas/${area.damageAreaCode}`, {}, toggledArea);
      dispatch(putDamageArea(toggledArea));
      dispatch(addMessage('success', 'itemStateUpdated', { item: area.damageAreaDescription }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
    dispatch(removePublishDamageArea(toggledArea.damageAreaCode));
  };
}
